import React, { useEffect, useState, forwardRef } from "react";
import '../../components/DashboardSeller/PendingQuotesDetails.css';
import { getPaymentTerms, getPreviewEnquiry, postSellerQuotation } from "../../API/Api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getAuthToken } from "../../utils/auth";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import QuotationShared from "./QuotationShared";
import { IoMdArrowRoundBack } from "react-icons/io";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import autoTable from 'jspdf-autotable';

const PendingQuotesDetails = ({ onBack, enquiryId }) => {
	const [enquiryData, setEnquiryData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [showSharedQuotation, setShowSharedQuotation] = useState(false);
	const [quotationResponse, setQuotationResponse] = useState(null);
	const [quotationAlreadySubmitted, setQuotationAlreadySubmitted] = useState(false);
	const navigate = useNavigate();
	const [errors, setErrors] = useState({});
	const [paymentTerms, setPaymentTerms] = useState([]);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [formData, setFormData] = useState({
		offerSuffix: '',
		offerDate: new Date().toISOString().split('T')[0],
		validityDate: '',
		paymentTerms: '',
		customPaymentTerms: '',
		gstType: '18',
		gstCustom: '',
		additionalNotes: '',
		terms: 'This quotation is valid for the next 2 days only. Pricing may change based on real-time market updates. Delivery time is approximate and subject to stock availability.',
		itemPrices: [],
		advancePaymentValue: '',
		advanceType: 'percentage',
		restPaidWithinDays: '',
		paymentTermDays: '',
		hasMaterialTestCertificate: false
	});

	const [charges, setCharges] = useState([{ amount: 0, description: "" }]);

	const [calculations, setCalculations] = useState({
		subtotal: 0,
		subtotalWithCharges: 0,
		gstAmount: 0,
		grandTotal: 0,
		itemTotals: []
	});

	const enquiryReference = enquiryData?.enquiry_reference_number || 'N/A';
	const submittedDate = enquiryData?.created_at ? new Date(enquiryData.created_at).toLocaleDateString() : 'N/A';

	const handleChargeChange = (index, field, value) => {
		const updatedCharges = [...charges];
		updatedCharges[index] = {
			...updatedCharges[index],
			[field]: field === "amount" ? parseFloat(value) || 0 : value
		};
		setCharges(updatedCharges);

		const newCalculations = calculateTotals(formData, updatedCharges);
		setCalculations(newCalculations);
	};

	const addChargeField = () => {
		setCharges([...charges, { amount: 0, description: "" }]);
	};

	const removeChargeField = (index) => {
		if (charges.length > 1) {
			const updatedCharges = [...charges];
			updatedCharges.splice(index, 1);
			setCharges(updatedCharges);

			const newCalculations = calculateTotals(formData, updatedCharges);
			setCalculations(newCalculations);
		}
	};

	const today = new Date();

	useEffect(() => {
		const fetchEnquiryData = async () => {
			try {
				const token = getAuthToken();
				const response = await getPreviewEnquiry(enquiryId, token);
				console.log("RESPONSE:::", response);
				setEnquiryData(response.data);

				if (response.data.latest_quotation) {
					setQuotationAlreadySubmitted(true);
				}

				setLoading(false);
			} catch (err) {
				setError(err.message || "Failed to fetch enquiry data");
				setLoading(false);
			}
		};

		fetchEnquiryData();
	}, [enquiryId]);

	const formatCurrency = (amount) => {
		return new Intl.NumberFormat('en-IN', {
			style: 'currency',
			currency: 'INR',
			minimumFractionDigits: 2
		}).format(amount);
	};

	useEffect(() => {
		const fetchPaymentTerms = async () => {
			try {
				const response = await getPaymentTerms();
				if (response.status) {
					setPaymentTerms(response.data);
				}
			} catch (error) {
				console.error("Error fetching payment terms:", error);
			}
		};

		fetchPaymentTerms();
	}, []);

	const calculateItemTotal = (price, unit, quantity, quantityUnit) => {
		const numericPrice = parseFloat(price) || 0;
		const numericQuantity = parseFloat(quantity) || 0;

		if (unit.includes(quantityUnit.toLowerCase()) ||
			(unit === 'per pc' && quantityUnit === 'Pc') ||
			(unit === 'per meter' && quantityUnit.toLowerCase() === 'meters')) {
			return numericPrice * numericQuantity;
		}

		if (unit === 'per MT' && quantityUnit.toLowerCase() === 'kg') {
			return numericPrice * (numericQuantity / 1000);
		}

		if (unit === 'per kg' && quantityUnit === 'MT') {
			return numericPrice * (numericQuantity * 1000);
		}

		return numericPrice * numericQuantity;
	};

	useEffect(() => {
		if (enquiryData) {
			const initialItemPrices = enquiryData.enquiry_items.map(item => {
				const latestPrice = item.latest_quotation?.price || '';
				const latestUnit = item.latest_quotation?.unit ||
					(item.quantity_unit === 'Pc' ? 'per pc' : 'per kg');
				const latestTotalPrice = item.latest_quotation?.total_price ||
					calculateItemTotal(latestPrice, latestUnit, item.universal_qty, item.quantity_unit);

				return {
					price: latestPrice,
					priceUnit: latestUnit,
					totalPrice: latestTotalPrice,
				};
			});

			const validityDate = new Date();
			validityDate.setDate(validityDate.getDate() + 2);

			let initialCharges = [{ amount: 0, description: "" }];
			if (enquiryData.latest_quotation?.other_charges_array) {
				let charges = enquiryData.latest_quotation.other_charges_array;
				if (typeof charges === 'string') {
					try {
						charges = JSON.parse(charges);
					} catch (e) {
						console.error('Invalid JSON in other_charges_array:', e);
						charges = [];
					}
				}

				if (Array.isArray(charges)) {
					initialCharges = charges.map(charge => ({
						amount: parseFloat(charge.amount) || 0,
						description: charge.description || ''
					}));
				}
			}

			const newFormData = {
				offerSuffix: enquiryData.latest_quotation?.offer_number || '',
				offerDate: new Date().toISOString().split('T')[0],
				validityDate: validityDate.toISOString().split('T')[0],
				paymentTerms: enquiryData.latest_quotation?.seller_payment_terms || '50% advance, 50% on delivery',
				customPaymentTerms: '',
				gstType: enquiryData.latest_quotation?.gst_percentage?.toString() || '18',
				gstCustom: '',
				additionalNotes: enquiryData.latest_quotation?.notes || '',
				terms: enquiryData.latest_quotation?.term_conditions ||
					'This quotation is valid for the next 2 days only. Pricing may change based on real-time market updates. Delivery time is approximate and subject to stock availability.',
				itemPrices: initialItemPrices,
				other_charges: initialCharges,
				advancePaymentValue: enquiryData.latest_quotation?.advance_payment_value || '',
				advanceType: enquiryData.latest_quotation?.advance_type || 'percentage',
				restPaidWithinDays: enquiryData.latest_quotation?.rest_paid_within_days || ''
			};

			setFormData(newFormData);
			setCharges(initialCharges);

			const initialCalculations = calculateTotals(newFormData, initialCharges);
			setCalculations(initialCalculations);
		}
	}, [enquiryData]);

	const calculateTotals = (formData, chargesArray) => {
		if (!enquiryData || !formData.itemPrices || formData.itemPrices.length === 0) {
			return {
				subtotal: 0,
				subtotalWithCharges: 0,
				gstAmount: 0,
				grandTotal: 0,
				itemTotals: []
			};
		}

		let subtotal = 0;
		const itemTotals = [];

		enquiryData.enquiry_items.forEach((item, index) => {
			const price = formData.itemPrices[index]?.price || 0;
			const unit = formData.itemPrices[index]?.priceUnit ||
				(item.quantity_unit === 'Pc' ? 'per pc' : 'per kg');

			const itemTotal = calculateItemTotal(price, unit, item.universal_qty, item.quantity_unit);

			subtotal += itemTotal;
			itemTotals.push(itemTotal);
		});

		const totalOtherCharges = chargesArray.reduce(
			(sum, charge) => sum + (parseFloat(charge.amount) || 0),
			0
		);

		const subtotalWithCharges = subtotal + totalOtherCharges;

		let gstPercent;
		if (formData.gstType === 'custom') {
			gstPercent = parseFloat(formData.gstCustom) || 0;
		} else {
			gstPercent = parseFloat(formData.gstType) || 0;
		}

		const gstAmount = subtotalWithCharges * (gstPercent / 100);
		const grandTotal = subtotalWithCharges + gstAmount;

		return {
			subtotal,
			subtotalWithCharges,
			gstAmount,
			grandTotal,
			itemTotals
		};
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => {
			const newFormData = { ...prev, [name]: value };
			const newCalculations = calculateTotals(newFormData, charges);
			setCalculations(newCalculations);
			return newFormData;
		});
	};

	const handleItemChange = (index, field, value) => {
		setFormData(prev => {
			const newItemPrices = [...prev.itemPrices];
			if (!newItemPrices[index]) {
				newItemPrices[index] = { price: '', priceUnit: '' };
			}
			newItemPrices[index] = { ...newItemPrices[index], [field]: value };

			if (field === 'price' || field === 'priceUnit') {
				const item = enquiryData.enquiry_items[index];
				const price = field === 'price' ? value : newItemPrices[index].price;
				const unit = field === 'priceUnit' ? value : newItemPrices[index].priceUnit;

				if (price && unit && item) {
					newItemPrices[index].totalPrice = calculateItemTotal(
						price,
						unit,
						item.universal_qty,
						item.quantity_unit
					);
				}
			}

			const newFormData = { ...prev, itemPrices: newItemPrices };
			const newCalculations = calculateTotals(newFormData, charges);
			setCalculations(newCalculations);
			return newFormData;
		});
	};

	const handleDateChange = (name, date) => {
		setFormData(prev => ({
			...prev,
			[name]: date.toISOString().split('T')[0]
		}));
	};

	const validateOtherCharges = () => {
		for (const charge of charges) {
			if ((charge.amount > 0 && !charge.description.trim()) ||
				(!charge.amount && charge.description.trim())) {
				return false;
			}
		}
		return true;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (isSubmitting || quotationAlreadySubmitted) return;
		setIsSubmitting(true);

		if (!formData.offerSuffix?.trim()) {
			toast.error("Offer Number suffix is a required field.");
			setIsSubmitting(false);
			return;
		}

		if (quotationAlreadySubmitted) {
			toast.error("A quotation has already been submitted for this enquiry.");
			return;
		}

		if (!formData.offerDate) {
			toast.error("Offer Date is a required field.");
			return;
		}

		if (!formData.validityDate) {
			toast.error("Valid Till date is a required field.");
			return;
		}

		if (!validateOtherCharges()) {
			toast.error("Please provide description for all charges with amounts");
			return;
		}

		let paymentTermsToSubmit = formData.paymentTerms;
		let paymentDetails = {};

		if (formData.paymentTerms === 'Advance') {
			paymentTermsToSubmit = `Advance (${formData.advancePaymentValue}`;

			paymentDetails = {
				advance_type: formData.advanceType,
				advance_payment_value: formData.advancePaymentValue,
				rest_paid_within_days: formData.restPaidWithinDays
			};
		} else if (formData.paymentTerms !== 'custom') {
			paymentDetails = {
				payment_term_number_days: formData.paymentTermDays
			};
		}

		// let advanceDetails = {};
		// if (formData.paymentTerms === 'Advance') {
		// 	paymentTermsToSubmit = `Advance (${formData.advancePaymentValue}${formData.advanceType === 'percentage' ? '%' : ''
		// 		}), rest within ${formData.restPaidWithinDays} days`;

		// 	advanceDetails = {
		// 		advance_type: formData.advanceType,
		// 		advance_payment_value: formData.advancePaymentValue,
		// 		rest_paid_within_days: formData.restPaidWithinDays
		// 	};
		// } else if (formData.paymentTerms === 'custom') {
		// 	paymentTermsToSubmit = formData.customPaymentTerms;
		// }

		try {
			const finalQuote = enquiryData.enquiry_items.map((item, index) => ({
				...item,
				unitPrice: formData.itemPrices[index].price,
				priceUnit: formData.itemPrices[index].priceUnit,
				itemTotal: calculations.itemTotals[index],
			}));

			const filteredCharges = charges.filter(charge =>
				charge.amount > 0 || charge.description.trim() !== ""
			);

			const totalOtherCharges = filteredCharges.reduce(
				(sum, charge) => sum + (parseFloat(charge.amount) || 0),
				0
			);

			const apiPayload = {
				enquiry_id: enquiryId,
				offer_details: {
					offer_number: `QTN-${new Date().getFullYear()}-${formData.offerSuffix}`,
					offer_date: formData.offerDate,
					validity_date: formData.validityDate,
				},
				...paymentDetails,
				payment_terms: paymentTermsToSubmit,
				enquiry_item: finalQuote.map(item => ({
					enquiry_item_id: item.enquiry_item_id,
					product_id: item.product_id,
					price: item.unitPrice,
					unit: item.priceUnit,
					quantity: item.universal_qty,
					total_price: item.itemTotal
				})),
				pricing_summary: {
					subtotal: calculations.subtotal,
					other_charges: filteredCharges,
					gst_details: {
						percentage: formData.gstType === 'custom' ? formData.gstCustom : formData.gstType,
						amount: calculations.gstAmount
					},
					grand_total: calculations.grandTotal
				},
				notes: formData.additionalNotes,
				gst: formData.gstType === 'custom' ? formData.gstCustom : formData.gstType,
				term_conditions: formData.terms,
				grand_total: calculations.grandTotal,
				other_charges: totalOtherCharges,
				other_charges_array: filteredCharges,
				offer_number: formData.offerSuffix,
				mtc: formData.hasMaterialTestCertificate
			};

			console.log("Submitting payload:", apiPayload);

			const response = await postSellerQuotation(apiPayload);

			toast.success('Quotation submitted successfully!', {
				position: "top-right",
				autoClose: 5000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
			});

			setQuotationResponse(response.data);
			setQuotationAlreadySubmitted(true);
			setShowSharedQuotation(true);

		} catch (error) {
			console.error('Submission Error:', error);
			toast.error(error?.message || 'Failed to submit quotation', {
				position: "top-right",
				autoClose: 5000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const CustomInput = forwardRef(({ value, onClick, placeholder }, ref) => (
		<input
			type="text"
			className="form-control"
			onClick={onClick}
			value={value}
			ref={ref}
			readOnly
			placeholder={placeholder}
		/>
	));
	// const handleDownloadPO = () =>   {
	// 	try {
	// 		if (!enquiryData || !formData) {
	// 			throw new Error("Enquiry data not loaded yet");
	// 		}

	// 		const doc = new jsPDF();

	// 		// HEADER
	// 		doc.setFontSize(18);
	// 		doc.setTextColor(91, 121, 171); // Blue
	// 		doc.text("PURCHASE ORDER", 150, 20, { align: "right" });

	// 		doc.setFontSize(11);
	// 		doc.setTextColor(0, 0, 0);
	// 		doc.text(`DATE: ${new Date().toLocaleDateString()}`, 150, 28, { align: "right" });
	// 		doc.text(`PO #: ${formData.offerSuffix}`, 150, 34, { align: "right" });

	// 		// VENDOR & SHIP TO INFO
	// 		doc.setFontSize(12);
	// 		doc.text("VENDOR", 20, 45);
	// 		doc.text("SHIP TO", 110, 45);

	// 		doc.setFontSize(10);
	// 		doc.text("[Company Name]", 20, 50);
	// 		doc.text("[Contact or Department]", 20, 55);
	// 		doc.text("[Street Address]", 20, 60);
	// 		doc.text("[City, ST ZIP]", 20, 65);
	// 		doc.text("Phone: (000) 000-0000", 20, 70);

	// 		doc.text("[Name]", 110, 50);
	// 		doc.text("[Company Name]", 110, 55);
	// 		doc.text("[Street Address]", 110, 60);
	// 		doc.text("[City, ST ZIP]", 110, 65);
	// 		doc.text("[Phone]", 110, 70);

	// 		// ITEM TABLE
	// 		const itemsData = enquiryData.enquiry_items.map((item, index) => {
	// 			const price = parseFloat(formData.itemPrices[index]?.price) || 0;
	// 			const total = parseFloat(formData.itemPrices[index]?.totalPrice) || 0;

	// 			// Create description with all product details
	// 			const description = [
	// 				`Product: ${item.product_name || '-'}`,
	// 				`Size: ${item.size || '-'}`,
	// 				`Wall Thickness: ${item.wall_thickness || '-'}`,
	// 				`Finish: ${item.finish || '-'}`,
	// 				`Specification: ${item.specification || '-'}`,
	// 				`End: ${item.end || '-'}`,
	// 				`Length: ${item.length || '-'}`,
	// 				`Price Basis: ${item.price_basis || '-'}`
	// 			].join('\n');

	// 			return [
	// 				item.product_id || '-', // Item #
	// 				{ content: description, styles: { cellWidth: 'wrap' } },
	// 				`${item.universal_qty} ${item.quantity_unit}`,
	// 				price.toFixed(2),
	// 				total.toFixed(2)
	// 			];
	// 		});

	// 		// Create the table with adjusted column widths
	// 		autoTable(doc, {
	// 			startY: 80,
	// 			head: [['Item #', 'Description', 'Quantity', 'Unit Price', 'Total']],
	// 			body: itemsData,
	// 			margin: { left: 20 },
	// 			columnStyles: {
	// 				0: { cellWidth: 20 },  // Item #
	// 				1: { cellWidth: 70 },  // Description (wider for details)
	// 				2: { cellWidth: 30 },  // Quantity
	// 				3: { cellWidth: 30 },  // Unit Price
	// 				4: { cellWidth: 30 }   // Total
	// 			},
	// 			styles: {
	// 				fontSize: 8,  // Smaller font to fit more content
	// 				cellPadding: 2,
	// 				overflow: 'linebreak'
	// 			},
	// 			didDrawCell: (data) => {
	// 				// Add some custom formatting if needed
	// 				if (data.section === 'body' && data.column.index === 1) {
	// 					// Description column
	// 					doc.setFontSize(7);
	// 				}
	// 			}
	// 		});

	// 		// Get the final Y position after the table
	// 		const finalY = doc.lastAutoTable.finalY + 10;

	// 		// SUBTOTALS
	// 		doc.setFontSize(11);
	// 		doc.text("SUBTOTAL:", 150, finalY);
	// 		doc.text(`₹${calculations.subtotal.toFixed(2)}`, 180, finalY, { align: "right" });

	// 		doc.text("TAX:", 150, finalY + 6);
	// 		doc.text(`₹${calculations.gstAmount.toFixed(2)}`, 180, finalY + 6, { align: "right" });

	// 		doc.text("OTHER:", 150, finalY + 12);
	// 		const otherCharges = charges.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
	// 		doc.text(`₹${otherCharges.toFixed(2)}`, 180, finalY + 12, { align: "right" });

	// 		doc.setFont(undefined, "bold");
	// 		doc.text("TOTAL:", 150, finalY + 20);
	// 		doc.text(`₹${calculations.grandTotal.toFixed(2)}`, 180, finalY + 20, { align: "right" });
	// 		doc.setFont(undefined, "normal");

	// 		// NOTES
	// 		doc.setFontSize(10);
	// 		doc.text("Comments or Special Instructions:", 20, finalY + 30);
	// 		doc.setDrawColor(0);
	// 		doc.rect(20, finalY + 32, 170, 20);
	// 		doc.text(formData.additionalNotes || "-", 22, finalY + 38);

	// 		// FOOTER
	// 		doc.setFontSize(9);
	// 		doc.text(
	// 			"If you have any questions about this purchase order, please contact [Name, Phone #, Email]",
	// 			20,
	// 			290
	// 		);

	// 		doc.save(`PO_${enquiryData.enquiry_reference_number || enquiryId}.pdf`);
	// 		toast.success("Purchase Order downloaded successfully");

	// 	} catch (error) {
	// 		console.error('PDF generation error:', error);
	// 		toast.error(`Failed to generate PO: ${error.message}`);
	// 	}
	// };

	// const handleDownloadPO = () => {
	// 	try {
	// 		if (!enquiryData || !formData) {
	// 			throw new Error("Enquiry data not loaded yet");
	// 		}

	// 		const doc = new jsPDF({
	// 			orientation: "portrait",
	// 			unit: "mm"
	// 		});

	// 		const margin = 15;
	// 		const pageWidth = 210 - (margin * 2);
	// 		const centerX = 210 / 2;
	// 		let currentY = 20;

	// 		doc.setFontSize(16);
	// 		doc.setFont(undefined, 'bold');
	// 		doc.setTextColor(0, 0, 0);
	// 		doc.text("QUOTATION DETAILS", centerX, currentY, { align: 'center' });
	// 		doc.setFont(undefined, 'normal');
	// 		currentY += 10;

	// 		const leftColX = margin;
	// 		const rightColX = centerX;
	// 		const labelWidth = 40;

	// 		doc.setFontSize(9);

	// 		doc.text("Enquiry Reference:", leftColX, currentY);
	// 		doc.text(enquiryData.enquiry_reference_number || 'N/A', leftColX + 30, currentY);

	// 		doc.text("Buyer Name:", leftColX, currentY + 5);
	// 		doc.text(enquiryData.buyer_name || 'ELECTRIC', leftColX + 30, currentY + 5);

	// 		doc.text("Submitted On:", leftColX, currentY + 5);
	// 		doc.text(enquiryData.created_at ? new Date(enquiryData.created_at).toLocaleDateString() : 'N/A', leftColX + 30, currentY + 5);

	// 		doc.text("Required Delivery:", leftColX, currentY + 10);
	// 		doc.text(`${enquiryData.delivery_time || ''}${enquiryData.delivery_time_other ? ` (${enquiryData.delivery_time_other})` : ''}`, leftColX + 30, currentY + 10);

	// 		doc.text("Payment Terms:", leftColX, currentY + 15);
	// 		doc.text(`${enquiryData.payment_term || ''}${enquiryData.payment_term_number_days ? ` (${enquiryData.payment_term_number_days} days)` : ''}`, leftColX + 30, currentY + 15);

	// 		doc.text("Location:", leftColX, currentY + 20);
	// 		const location = [enquiryData.enquiry_items[0]?.address, enquiryData.enquiry_items[0]?.pincode].filter(Boolean).join(', ');
	// 		doc.text(location || 'N/A', leftColX + 30, currentY + 20);

	// 		doc.text("Offer Number:", rightColX, currentY);
	// 		doc.text(`QTN-${new Date().getFullYear()}-${formData.offerSuffix}`, rightColX + 30, currentY);

	// 		doc.text("Offer Date:", rightColX, currentY + 5);
	// 		doc.text(formData.offerDate ? new Date(formData.offerDate).toLocaleDateString() : 'N/A', rightColX + 30, currentY + 5);

	// 		doc.text("Valid Till:", rightColX, currentY + 10);
	// 		doc.text(formData.validityDate ? new Date(formData.validityDate).toLocaleDateString() : 'N/A', rightColX + 30, currentY + 10);

	// 		doc.text("Payment Terms:", rightColX, currentY + 15);
	// 		const paymentTermsText = formData.paymentTerms === 'custom'
	// 			? formData.customPaymentTerms
	// 			: formData.paymentTerms;
	// 		doc.text(paymentTermsText || 'N/A', rightColX + 30, currentY + 15);

	// 		doc.text("GST %:", rightColX, currentY + 20);
	// 		doc.text(`${formData.gstType}%`, rightColX + 30, currentY + 20);

	// 		currentY += 25;

	// 		doc.setDrawColor(200, 200, 200);
	// 		doc.line(margin, currentY, 210 - margin, currentY);
	// 		currentY += 5;

	// 		const itemsData = enquiryData.enquiry_items.map((item, index) => {
	// 			const price = parseFloat(formData.itemPrices[index]?.price) || 0;
	// 			const total = parseFloat(formData.itemPrices[index]?.totalPrice) || 0;

	// 			return [
	// 				index + 1,
	// 				item.product_name || '-',
	// 				item.size || '-',
	// 				item.wall_thickness || '-',
	// 				item.finish || '-',
	// 				item.specification || '-',
	// 				item.end || '-',
	// 				item.length || '-',
	// 				item.price_basis || '-',
	// 				`${item.universal_qty} ${item.quantity_unit}`,
	// 				`₹${price.toFixed(2)}`,
	// 				`₹${total.toFixed(2)}`
	// 			];
	// 		});

	// 		autoTable(doc, {
	// 			startY: currentY,
	// 			head: [
	// 				[
	// 					'Sr No',
	// 					'Product',
	// 					'Size',
	// 					'Thickness',
	// 					'Finish',
	// 					'Spec',
	// 					'End',
	// 					'Length',
	// 					'Basis',
	// 					'Qty',
	// 					'Unit Price',
	// 					'Total'
	// 				]
	// 			],
	// 			body: itemsData,
	// 			margin: { left: margin, right: margin },
	// 			styles: {
	// 				fontSize: 7,
	// 				cellPadding: 1,
	// 				lineColor: [200, 200, 200],
	// 				lineWidth: 0.2,
	// 				textColor: [0, 0, 0],
	// 				valign: 'middle'
	// 			},
	// 			headStyles: {
	// 				fillColor: [0, 43, 92],
	// 				textColor: [255, 255, 255],
	// 				fontStyle: 'bold'
	// 			},
	// 			columnStyles: {
	// 				0: { cellWidth: 6, halign: 'center' },
	// 				1: { cellWidth: 18 },
	// 				2: { cellWidth: 10 },
	// 				3: { cellWidth: 18 },
	// 				4: { cellWidth: 18 },
	// 				5: { cellWidth: 18 },
	// 				6: { cellWidth: 18 },
	// 				7: { cellWidth: 15 },
	// 				8: { cellWidth: 18 },
	// 				9: { cellWidth: 18, halign: 'center' },
	// 				10: { cellWidth: 18, halign: 'center' },
	// 				11: { cellWidth: 18, halign: 'center' }
	// 			},
	// 			didDrawPage: function (data) {
	// 				doc.setFontSize(8);
	// 				doc.setTextColor(100, 100, 100);
	// 				doc.text(
	// 					`Page ${data.pageNumber} of ${data.pageCount}`,
	// 					margin,
	// 					doc.internal.pageSize.height - 10
	// 				);
	// 			}
	// 		});

	// 		const finalY = doc.lastAutoTable.finalY + 10;

	// 		doc.setFontSize(9);
	// 		doc.setTextColor(0, 0, 0);

	// 		doc.text("Subtotal:", 210 - margin - 60, finalY);
	// 		doc.text(`₹${calculations.subtotal.toFixed(2)}`, 210 - margin, finalY, { align: "right" });

	// 		doc.text(`GST (${formData.gstType}%):`, 210 - margin - 60, finalY + 6);
	// 		doc.text(`₹${calculations.gstAmount.toFixed(2)}`, 210 - margin, finalY + 6, { align: "right" });

	// 		const otherCharges = charges.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
	// 		doc.text("Other Charges:", 210 - margin - 60, finalY + 12);
	// 		doc.text(`₹${otherCharges.toFixed(2)}`, 210 - margin, finalY + 12, { align: "right" });

	// 		doc.setFont(undefined, 'bold');
	// 		doc.text("Grand Total:", 210 - margin - 60, finalY + 20);
	// 		doc.text(`₹${calculations.grandTotal.toFixed(2)}`, 210 - margin, finalY + 20, { align: "right" });
	// 		doc.setFont(undefined, 'normal');

	// 		doc.setDrawColor(200, 200, 200);
	// 		doc.line(margin, finalY + 25, 210 - margin, finalY + 25);

	// 		doc.setFontSize(9);
	// 		doc.text("Notes & Terms:", margin, finalY + 30);
	// 		doc.setFontSize(8);
	// 		doc.text(formData.additionalNotes || "No additional notes", margin, finalY + 35, {
	// 			maxWidth: pageWidth,
	// 			align: 'left'
	// 		});

	// 		doc.setFontSize(9);
	// 		doc.text("Terms & Conditions:", margin, finalY + 45);
	// 		doc.setFontSize(8);
	// 		const splitTerms = doc.splitTextToSize(formData.terms || "Standard terms apply", pageWidth);
	// 		doc.text(splitTerms, margin, finalY + 50);

	// 		// doc.save(`PO_${enquiryData.enquiry_reference_number || enquiryId}.pdf`);
	// 		doc.save(`PO.pdf`);
	// 		toast.success("Quotation downloaded successfully");

	// 	} catch (error) {
	// 		console.error('PDF generation error:', error);
	// 		toast.error(`Failed to generate quotation: ${error.message}`);
	// 	}
	// };

	const handleDownloadPO = () => {
		try {
			if (!enquiryData) {
				throw new Error("Enquiry data not loaded yet");
			}

			if (!enquiryData.admin_po_file) {
				toast.info("No PO file available for download");
				return;
			}

			const link = document.createElement('a');
			link.href = enquiryData.admin_po_file;
			link.target = '_blank';
			link.download = `PO_${enquiryData.order_id || enquiryId}.pdf`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			toast.success("PO file downloaded successfully");
		} catch (error) {
			console.error('Download error:', error);
			toast.error(`Failed to download PO: ${error.message}`);
		}
	};

	if (loading) {
		return <div className="container">Loading...</div>;
	}

	if (error) {
		return <div className="container">Error: {error}</div>;
	}

	if (!enquiryData) {
		return <div className="container">No enquiry data found</div>;
	}

	return (
		<div className="container pending-quotes-details seller-quote-view">
			<ToastContainer />
			{showSharedQuotation ? (
				<QuotationShared
					onBack={() => setShowSharedQuotation(false)}
					quotationData={quotationResponse}
				/>
			) : (
				<main>
					<div className="card">
						<div className="card-header">
							<h2>Quotation Request for {enquiryData.enquiry_items[0]?.product_name || 'Product'}</h2>
							<IoMdArrowRoundBack onClick={onBack} />
						</div>

						<div className="card-body">
							<div className="info-grid mb-6 seller-details-header">
								<div className="info-section">
									<h3 className="mb-4">Enquiry Details</h3>

									{enquiryData.enquiry_reference_number && (
										<div className="info-row">
											<span className="info-label">Enquiry Reference:</span>
											<span className="info-value">{enquiryData.enquiry_reference_number}</span>
										</div>
									)}

									{enquiryData.created_at && (
										<div className="info-row">
											<span className="info-label">Buyer Name:</span>
											<span className="info-value">STELMART</span>
										</div>
									)}

									{enquiryData.created_at && (
										<div className="info-row">
											<span className="info-label">Submitted On:</span>
											<span className="info-value">{new Date(enquiryData.created_at).toLocaleDateString()}</span>
										</div>
									)}

									{enquiryData.delivery_time && (
										<div className="info-row">
											<span className="info-label">Required Delivery:</span>
											<span className="info-value">
												{enquiryData.delivery_time}
												{enquiryData.delivery_time_other && ` (${enquiryData.delivery_time_other})`}
											</span>
										</div>
									)}

									{enquiryData.payment_term && (
										<div className="info-row">
											<span className="info-label">Payment Terms:</span>
											<span className="info-value">
												{enquiryData.payment_term}
												{enquiryData.payment_term_number_days && ` (${enquiryData.payment_term_number_days} days)`}
												{enquiryData.advance_type && (
													<>
														<br />
														<small>
															Advance Type: {enquiryData.advance_type} <br />
															Advance Payment Value — {enquiryData.advance_payment_value}
															{enquiryData.advance_type === 'Percentage' && '%'} <br />
															{enquiryData.rest_paid_within_days && (
																<>Rest payable within :{enquiryData.rest_paid_within_days} </>
															)}
														</small>
													</>
												)}
											</span>
										</div>
									)}

									{(enquiryData.enquiry_items?.[0]?.address || enquiryData.enquiry_items?.[0]?.pincode) && (
										<div className="info-row">
											<span className="info-label">Location:</span>
											<span className="info-value">
												{[enquiryData.enquiry_items[0]?.address, enquiryData.enquiry_items[0]?.pincode]
													.filter(Boolean)
													.join(', ')}
											</span>
										</div>
									)}
									<div className="info-row mt-6">
										<button
											className="payment-modal-confirm-po font-poppins"
											onClick={handleDownloadPO}
										>
											Download PO
										</button>
									</div>
								</div>

								<div className="info-section">
									<h3 className="mb-4">Your Quotation Details</h3>
									<div className="row">
										<div className="info-row">
											<span className="info-label">Offer Number:</span>
											<span className="info-value">
												QTN-{new Date().getFullYear()}-
												<input
													type="text"
													name="offerSuffix"
													placeholder="Enter offer number suffix"
													className={`form-control ${errors.offerSuffix ? 'is-invalid' : ''}`}
													style={{ display: 'inline-block', width: 'auto' }}
													value={formData.offerSuffix}
													onChange={handleChange}
													required
													disabled={quotationAlreadySubmitted}
												/>
												{errors.offerSuffix && (
													<div className="invalid-feedback" style={{ color: 'red', fontSize: '12px' }}>
														{errors.offerSuffix}
													</div>
												)}
											</span>
										</div>
										<div className="info-row">
											<span className="info-label">Offer Date:</span>
											<span className="info-value">
												<DatePicker
													selected={formData.offerDate ? new Date(formData.offerDate) : null}
													onChange={(date) => handleDateChange("offerDate", date)}
													name="offerDate"
													dateFormat="dd/MM/yyyy"
													minDate={today}
													required
													placeholderText="Select offer date"
													customInput={<CustomInput placeholder="Select offer date" />}
													disabled={quotationAlreadySubmitted}
												/>
												{errors.offerDate && (
													<div className="invalid-feedback" style={{ color: 'red', fontSize: '12px' }}>
														{errors.offerDate}
													</div>
												)}
											</span>
										</div>

										<div className="info-row">
											<span className="info-label">Valid Till:</span>
											<span className="info-value">
												<DatePicker
													selected={formData.validityDate ? new Date(formData.validityDate) : null}
													onChange={(date) => handleDateChange("validityDate", date)}
													name="validityDate"
													dateFormat="dd/MM/yyyy"
													minDate={new Date(formData.offerDate || new Date())}
													required
													placeholderText="Select valid till date"
													customInput={<CustomInput placeholder="Select valid till date" />}
													disabled={quotationAlreadySubmitted}
												/>
												{errors.validityDate && (
													<div className="invalid-feedback" style={{ color: 'red', fontSize: '12px' }}>
														{errors.validityDate}
													</div>
												)}
											</span>
										</div>

										<div className="info-row-sel">
											<span className="info-label-sel">Payment Terms:</span>
											<span className="info-value-sel">
												<select
													id="paymentTerms"
													className="info-input-sel"
													name="paymentTerms"
													value={formData.paymentTerms}
													onChange={handleChange}
													disabled={quotationAlreadySubmitted}
												>
													<option value="">Select payment term</option>
													{paymentTerms.map((term) => (
														<option key={term.id} value={term.name.trim()}>
															{term.name.trim()}
														</option>
													))}
												</select>

												{formData.paymentTerms === 'custom' && (
													<input
														type="text"
														className="info-input-sel"
														name="customPaymentTerms"
														value={formData.customPaymentTerms || ''}
														onChange={handleChange}
														placeholder="Enter custom payment terms"
														disabled={quotationAlreadySubmitted}
													/>
												)}

												{formData.paymentTerms === 'Advance' && (
													<div className="advance-fields-container-sel">
														<div className="advance-field-sel gap-right-sel">
															<label className="info-label-sel">Advance Payment Value:</label>
															<input
																type="number"
																className="info-input-sel"
																name="advancePaymentValue"
																value={formData.advancePaymentValue}
																onChange={handleChange}
																disabled={quotationAlreadySubmitted}
															/>
														</div>

														<div className="advance-field-sel">
															<label className="info-label-sel">Advance Type:</label>
															<select
																className="info-input-sel"
																name="advanceType"
																value={formData.advanceType}
																onChange={handleChange}
																disabled={quotationAlreadySubmitted}
															>
																<option value="percentage">Percentage</option>
																<option value="fixed">Fixed Amount</option>
															</select>
														</div>

														<div className="advance-field-sel">
															<label className="info-label-sel">Rest paid within (days):</label>
															<input
																type="number"
																className="info-input-sel sel-inpt"
																name="restPaidWithinDays"
																value={formData.restPaidWithinDays}
																onChange={handleChange}
																min="1"
																placeholder="Enter days"
																disabled={quotationAlreadySubmitted}
															/>
														</div>
													</div>
												)}

												{formData.paymentTerms && formData.paymentTerms !== 'custom' && formData.paymentTerms !== 'Advance' && (
													<div className="days-input-container-sel">
														<label className="info-label-sel">Number of days:</label>
														<input
															type="number"
															className="info-input-sel days-input-sel"
															name="paymentTermDays"
															value={formData.paymentTermDays}
															onChange={handleChange}
															min="1"
															placeholder="Enter days"
															disabled={quotationAlreadySubmitted}
														/>
													</div>
												)}
											</span>
										</div>
										<div className="">
											<span className="info-label-sel">Material Test Certificate (MTC):</span>
											<span className="info-value-sel">
												<div className="mtc-radio-container">
													<label className="mtc-radio-label">
														<input
															type="radio"
															name="hasMaterialTestCertificate"
															value={true}
															checked={formData.hasMaterialTestCertificate === true}
															onChange={() => handleChange({ target: { name: 'hasMaterialTestCertificate', value: true } })}
															disabled={quotationAlreadySubmitted}
														/>
														<span>Yes</span>
													</label>

													<label className="mtc-radio-label">
														<input
															type="radio"
															name="hasMaterialTestCertificate"
															value={false}
															checked={formData.hasMaterialTestCertificate === false}
															onChange={() => handleChange({ target: { name: 'hasMaterialTestCertificate', value: false } })}
															disabled={quotationAlreadySubmitted}
														/>
														<span>No</span>
													</label>
												</div>
											</span>
										</div>
									</div>
								</div>
							</div>

							<table className="table seller-item-table">
								<thead>
									<tr>
										<th>#</th>
										<th>Product Name</th>
										<th>Size</th>
										<th>Wall Thickness</th>
										<th>Finish</th>
										<th>Specification</th>
										<th>End</th>
										<th>Length</th>
										<th>Price Basis</th>
										<th>Quantity</th>
										<th>Your Price</th>
										<th>Unit</th>
										<th>Item Total</th>
									</tr>
								</thead>
								<tbody>
									{enquiryData.enquiry_items.map((item, index) => (
										<tr key={item.enquiry_item_id || index}>
											<td>{index + 1}</td>
											<td>{item.product_name}</td>
											<td>{item.size}</td>
											<td>{item.wall_thickness}</td>
											<td>{item.finish}</td>
											<td>{item.specification}</td>
											<td>{item.end}</td>
											<td>{item.length}</td>
											<td>{item.price_basis}</td>
											<td>{item.universal_qty} {item.quantity_unit}</td>
											<td>
												<input
													type="number"
													min="0"
													step="0.01"
													className="form-control"
													value={formData.itemPrices[index]?.price || ''}
													onChange={(e) => handleItemChange(index, 'price', e.target.value)}
													placeholder="Enter price"
													required
													disabled={quotationAlreadySubmitted}
												/>
											</td>
											<td>

												<select
													className="form-control"
													value={formData.itemPrices[index]?.priceUnit || ''}
													onChange={(e) => handleItemChange(index, 'priceUnit', e.target.value)}
													disabled={quotationAlreadySubmitted}
												>
													<option value="per kg">(₹/kg)</option>
													<option value="per MT">(₹/MT)</option>
													<option value="per pc">(₹/pc)</option>
													<option value="per meter">(₹/m)</option>
													<option value="per foot">(₹/ft)</option>
												</select>
											</td>
											<td>
												₹ {formData.itemPrices[index]?.totalPrice ?
													parseFloat(formData.itemPrices[index].totalPrice).toFixed(2) :
													'0.00'}
											</td>
										</tr>
									))}
								</tbody>
							</table>

							<div className="card mb-4">
								<div className="card-header">
									<h3>Other Charges</h3>
								</div>
								<div className="card-body">
									{charges.map((charge, index) => (
										<div key={index} className="row mb-3 other-charges-div-col">
											<div className="col-md-3">
												<input
													min="0"
													placeholder="Amount"
													className="form-control"
													type="number"
													value={charge.amount || ''}
													onChange={(e) => handleChargeChange(index, "amount", e.target.value)}
													disabled={quotationAlreadySubmitted}
												/>
											</div>
											<div className="col-md-7">
												<input
													placeholder="Description"
													className="form-control"
													type="text"
													value={charge.description}
													onChange={(e) => handleChargeChange(index, "description", e.target.value)}
													disabled={quotationAlreadySubmitted}
												/>
											</div>
											<div className="col-md-2">
												{charges.length > 1 && !quotationAlreadySubmitted && (
													<button
														type="button"
														className="btn btn-danger btn-block"
														onClick={() => removeChargeField(index)}
													>
														Remove
													</button>
												)}
											</div>
										</div>
									))}
									{!quotationAlreadySubmitted && (
										<button
											type="button"
											className="btn btn-secondary"
											onClick={addChargeField}
										>
											+ Add Charge
										</button>
									)}
								</div>
							</div>

							{/* Summary Section */}
							<div className="card">
								<div className="card-header">
									<h3>Quotation Summary</h3>
								</div>
								<div className="card-body">

									<div className="col-line-taxes">
										<div className="row mb-2">
											<div className="col-md-6 font-weight-bold">Subtotal:</div>
											<div className="col-md-6 text-right">{formatCurrency(calculations.subtotal)}</div>
										</div>

										<div className="row mb-2">
											<div className="col-md-6 font-weight-bold">Other Charges:</div>
											<div className="col-md-6 text-right">
												{formatCurrency(calculations.subtotalWithCharges - calculations.subtotal)}
											</div>
										</div>

										<div className="row mb-3">
											<div className="col-md-6 font-weight-bold">Subtotal with Charges:</div>
											<div className="col-md-6 text-right">{formatCurrency(calculations.subtotalWithCharges)}</div>
										</div>
									</div>
									<div className="row mb-3 taxes-slab-div">
										<div className="col-md-4">
											{/* <label>GST {formData.gstType} (%)</label> */}
											<label>GST </label>

											<div className="input-group">
												<select
													className="form-control"
													name="gstType"
													value={formData.gstType}
													onChange={handleChange}
													disabled={quotationAlreadySubmitted}
												>
													<option value="18.00">18%</option>
													<option value="custom">Custom</option>

													{/* <option value="0.10">0.10%</option>
													<option value="5.00">5%</option>
													<option value="12.00">12%</option>
													<option value="18.00">18%</option>
													<option value="28">28%</option>
													<option value="custom">Custom</option> */}
												</select>
												{formData.gstType === 'custom' && (
													<input
														type="number"
														className="form-control"
														min="0"
														max="100"
														step="0.01"
														placeholder="Enter %"
														name="gstCustom"
														value={formData.gstCustom}
														onChange={handleChange}
														disabled={quotationAlreadySubmitted}
													/>
												)}
											</div>
										</div>
										<div className="col-md-4">
											<label>GST Amount</label>
											<input
												type="text"
												className="form-control"
												value={formatCurrency(calculations.gstAmount)}
												readOnly
											/>
										</div>
										<div className="col-md-4">
											<label>Grand Total</label>
											<input
												type="text"
												className="form-control font-weight-bold"
												value={formatCurrency(calculations.grandTotal)}
												readOnly
											/>
										</div>
									</div>

									<div className="form-group mt-4">
										<label>Additional Notes</label>
										<textarea
											rows="2"
											placeholder="e.g., bulk discounts, material certs..."
											className="form-control"
											name="additionalNotes"
											value={formData.additionalNotes}
											onChange={handleChange}
											disabled={quotationAlreadySubmitted}
										></textarea>
									</div>

									<div className="form-group mt-4">
										<label>Terms & Conditions</label>
										<textarea
											rows="4"
											className="form-control"
											name="terms"
											value={formData.terms}
											onChange={handleChange}
											disabled={quotationAlreadySubmitted}
										></textarea>
									</div>

									<div className="form-group mt-4">
										<button
											type="submit"
											className="btn btn-primary btn-block"
											onClick={handleSubmit}
											disabled={quotationAlreadySubmitted || isSubmitting}
										>
											{quotationAlreadySubmitted
												? 'Quotation Already Submitted'
												: isSubmitting
													? 'Submitting...'
													: 'Submit Quotation'}
										</button>

										{quotationAlreadySubmitted && (
											<div className="alert alert-info mt-3">
												A quotation has already been submitted for this enquiry. You cannot submit another one.
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</main>
			)}
		</div>
	);
};

export default PendingQuotesDetails;